import type {
  OperationCancellationReason,
  OperationDefinition,
  OperationGroup,
  OperationHandle,
  OperationOwner,
  OperationOwnerDefinition,
} from './operation.types'
import {
  OperationHandleImpl,
  type OperationHandleDependencies,
  type OperationTimelineEntryInput,
} from './operation.handle'

/**
 * What `OperationOwnerImpl` needs from `OperationRepository` — kept as a
 * narrow interface so operation.owner.ts and operation.handle.ts do not
 * import operation.repository.ts (avoids a require cycle).
 */
export interface OperationOwnerHost {
  readonly generateUnitId: () => string
  readonly log: (message: string, context: Record<string, unknown>) => void
  readonly after: (delayMs: number, callback: () => void) => () => void
  readonly onGlobalUnitStart: (unitId: string, groups: readonly OperationGroup[]) => void
  readonly onGlobalUnitEnd: (unitId: string) => void
  readonly onTimelineEntry: (entry: OperationTimelineEntryInput) => void
  registerDefinition(definition: OperationDefinition<never, never>, registeredByOwnerId: string): void
  trackHandle(handle: OperationHandleImpl<never, never>): void
  untrackHandle(handle: OperationHandleImpl<never, never>): void
}

export class OperationOwnerImpl implements OperationOwner {
  private readonly runtimeId: string
  private readonly registeredOperationIds = new Set<string>()
  private readonly handles: OperationHandleImpl<never, never>[] = []
  private disposed = false
  private readonly ownerDefinition: OperationOwnerDefinition
  private readonly host: OperationOwnerHost

  constructor(ownerDefinition: OperationOwnerDefinition, host: OperationOwnerHost) {
    this.ownerDefinition = ownerDefinition
    this.host = host
    this.runtimeId = host.generateUnitId()
  }

  get diagnosticOwnerId(): string {
    return `${this.ownerDefinition.id}#${this.runtimeId}`
  }

  register<Args, Failure>(definition: OperationDefinition<Args, Failure>): OperationHandle<Args, Failure> {
    if (this.disposed) {
      throw new Error(`OperationOwner(${this.diagnosticOwnerId}): cannot register() after dispose().`)
    }
    if (this.registeredOperationIds.has(definition.id)) {
      throw new Error(
        `OperationOwner(${this.diagnosticOwnerId}): "${definition.id}" is already registered on this owner.`,
      )
    }
    this.registeredOperationIds.add(definition.id)
    this.host.registerDefinition(definition as unknown as OperationDefinition<never, never>, this.ownerDefinition.id)

    const deps: OperationHandleDependencies = {
      generateUnitId: this.host.generateUnitId,
      log: this.host.log,
      after: this.host.after,
      onGlobalUnitStart: this.host.onGlobalUnitStart,
      onGlobalUnitEnd: this.host.onGlobalUnitEnd,
      onTimelineEntry: this.host.onTimelineEntry,
    }
    const handle = new OperationHandleImpl<Args, Failure>(
      definition,
      this.diagnosticOwnerId,
      this.ownerDefinition.lifetime,
      deps,
    )
    this.handles.push(handle as unknown as OperationHandleImpl<never, never>)
    this.host.trackHandle(handle as unknown as OperationHandleImpl<never, never>)
    return handle
  }

  cancelAll(reason: OperationCancellationReason): void {
    this.handles.forEach((handle) => handle.cancelAllInternal(reason))
  }

  resetAll(reason: OperationCancellationReason): void {
    this.handles.forEach((handle) => handle.resetAll(reason))
  }

  dispose(): void {
    if (this.disposed) return
    this.handles.forEach((handle) => {
      handle.disposeHandle({ kind: 'owner-disposed' })
      this.host.untrackHandle(handle)
    })
    this.disposed = true
  }
}
